import React, { useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import 'chartjs-adapter-moment';
import { Calendar } from 'react-multi-date-picker';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    TimeScale,
    BarElement,
    ArcElement,
} from 'chart.js';
import {useParams} from "react-router-dom";
import '../../css/SessionChart.css';
import '../../css/CalendarCustom.css';
import {useProject} from "../../utils/ProjectContext";

ChartJS.register(
    BarElement,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    TimeScale,
    ArcElement
);

const formatDate = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const SessionChart = () => {
    const { BASE_URL, VISION_URL } = useProject();
    const { projectId } = useParams();
    const token = localStorage.getItem('token');
    const [sessionData, setSessionData] = useState([]);
    const [sessionChartData, setSessionChartData] = useState({ datasets: [] });
    const [currentStart, setCurrentStart] = useState(() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
    });
    const [currentEnd, setCurrentEnd] = useState(() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
    });

    // Calculate number of days between start and end dates
    const getDaysBetween = (start, end) => {
        return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    };

    const formatDateTime = (date) => {
        return `${formatDate(date)}T${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`;
    };
    const [calendarData, setCalendarData] = useState([]);

    const [sessionChartOptions, setSessionChartOptions] = useState(() => {
        // Initialize with single day view
        return {
            scales: {
                x: {
                    barPercentage: 0.8,
                    type: 'time',
                    bounds: 'ticks',
                    offset: true,
                    time: {
                        unit: 'hour',
                        tooltipFormat: 'MMM d, yyyy HH:mm',
                        displayFormats: {
                            hour: 'HH:mm',
                            day: 'MMM D'
                        },
                        stepSize: 2,
                        timezone: 'local',
                    },
                    grid: {
                        display: true
                    },
                    ticks: {
                        maxRotation: 0,
                        minRotation: 0,
                        autoSkip: true,
                        maxTicksLimit: 12,
                        callback: function(value) {
                            const date = new Date(value);
                            return date.getHours().toString().padStart(2, '0') + ':' + date.getMinutes().toString().padStart(2, '0');
                        }
                    }
                },
                y: {
                    beginAtZero: true,
                    title: {
                        display: true,
                        text: 'Duration'
                    },
                    min: 0,
                    suggestedMax: undefined,
                    ticks: {
                        precision: 1,
                        callback: function(value) {
                            if (value >= 60) {
                                return Math.round(value / 60) + 'm';
                            }
                            return value + 's';
                        }
                    }
                },
            },
            plugins: {
                tooltip: {
                    callbacks: {
                        title: function(context) {
                            const data = context[0].raw;
                            return `${data.context}: ${data.dataName}`;
                        },
                        label: function(context) {
                            const data = context.raw;
                            const minutes = Math.floor(data.y / 60);
                            const seconds = Math.round(data.y % 60);
                            const durationStr = minutes > 0 ? 
                                `Duration: ${minutes}m ${seconds}s` : 
                                `Duration: ${seconds}s`;
                            return [
                                durationStr,
                                `Feedback: ${data.feedback.charAt(0).toUpperCase() + data.feedback.slice(1)}`
                            ];
                        }
                    }
                },
                legend: {
                    display: true,
                    position: 'top'
                }
            },
            responsive: true,
            maintainAspectRatio: false,
            height: 80,
        };
    });

    const updateChartOptions = (start, end) => {
        const days = getDaysBetween(start, end);
        let unit, tickCallback, maxTicks;

        if (days <= 1) {
            // Single day - show hours with 2-hour intervals
            unit = 'hour';
            maxTicks = 12;
            tickCallback = (value) => {
                const date = new Date(value);
                const hours = date.getHours();
                const minutes = date.getMinutes();
                return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
            };
        } else if (days <= 7) {
            // Up to a week - show day names
            unit = 'day';
            maxTicks = Math.max(days * 2, 7); // Ensure we show all days
            tickCallback = (value) => {
                const date = new Date(value);
                const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                return days[date.getDay()];
            };
        } else if (days <= 35) {
            // Up to 5 weeks - show short day names
            unit = 'day';
            maxTicks = Math.max(days * 2, 7); // Ensure we show all days
            tickCallback = (value) => {
                const date = new Date(value);
                const days = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
                return days[date.getDay()];
            };
        } else {
            // More than 5 weeks - show months
            unit = 'month';
            maxTicks = Math.ceil(days / 30);
            tickCallback = (value) => {
                const date = new Date(value);
                const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                return months[date.getMonth()];
            };
        }

        setSessionChartOptions(prevOptions => ({
            ...prevOptions,
            scales: {
                ...prevOptions.scales,
                x: {
                    ...prevOptions.scales.x,
                    min: new Date(start.getFullYear(), start.getMonth(), start.getDate(), 0, 0, 0),
                    max: new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59),
                    bounds: 'ticks',
                    time: {
                        ...prevOptions.scales.x.time,
                        unit
                    },
                    ticks: {
                        ...prevOptions.scales.x.ticks,
                        maxTicksLimit: maxTicks,
                        callback: tickCallback
                    }
                }
            }
        }));
    };

    const fetchCalendarData = async () => {
        // Get first day of first visible month and last day of last visible month
        const firstMonth = new Date(currentStart);
        firstMonth.setDate(1);
        firstMonth.setMonth(firstMonth.getMonth() - 1); // One month before

        const lastMonth = new Date(currentStart);
        lastMonth.setMonth(lastMonth.getMonth() + 2); // Two months after
        lastMonth.setDate(0); // Last day of the month
        lastMonth.setHours(23, 59, 59, 999);

        try {
            const response = await fetch(
                `${VISION_URL}/api/analytics/projects/${projectId}?startDate=${formatDateTime(firstMonth)}&endDate=${formatDateTime(lastMonth)}`,
                {
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    },
                }
            );

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            setCalendarData(data.sessions || []);
        } catch (error) {
            console.error('Error fetching calendar data:', error);
            setCalendarData([]);
        }
    };

    useEffect(() => {
        fetchCalendarData();
    }, [currentStart]);

    const updateCurrentPeriod = (direction) => {
        // Get the current range in days
        const days = getDaysBetween(currentStart, currentEnd);
        
        // Create new dates by shifting the current range
        const newStart = new Date(currentStart);
        const newEnd = new Date(currentEnd);
        
        if (days <= 1) {
            // For single day, move by one day
            newStart.setDate(newStart.getDate() + direction);
            newEnd.setDate(newEnd.getDate() + direction);
            // Ensure times are set correctly
            newStart.setHours(0, 0, 0, 0);
            // If end date is today, set to future time to include all data
            const today = new Date();
            if (newEnd.toDateString() === today.toDateString()) {
                newEnd.setHours(23, 59, 59, 999);
            } else {
                newEnd.setHours(23, 59, 59, 999);
            }
        } else {
            // For multi-day selections, move by the range
            newStart.setDate(newStart.getDate() + (days * direction));
            newEnd.setDate(newEnd.getDate() + (days * direction));
        }
        
        setCurrentStart(newStart);
        setCurrentEnd(newEnd);
        updateChartOptions(newStart, newEnd);
    };

    const handlePrevious = () => {
        updateCurrentPeriod(-1);
    };

    const handleNext = () => {
        updateCurrentPeriod(1);
    };

    const fetchSessionData = async (projectId) => {
        try {
            const response = await fetch(
                `${VISION_URL}/api/analytics/projects/${projectId}?startDate=${formatDateTime(currentStart)}&endDate=${formatDateTime(currentEnd)}`,
                {
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (!data || !data.sessions || !Array.isArray(data.sessions)) {
                console.error('No valid session data found:', data);
                setSessionData([]);
                return;
            }

            // Transform sessions into the format needed for the chart
            const transformedData = {
                positive: [],
                negative: [],
                neutral: []
            };

            data.sessions.forEach(session => {
                const sessionData = {
                    x: (() => {
                        const date = new Date(session.timestamp);
                        // Create date at midnight in local timezone
                        const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(),
                                                 date.getHours(), date.getMinutes(), date.getSeconds());
                        // Adjust for Chart.js UTC handling
                        return new Date(localDate.getTime() + localDate.getTimezoneOffset() * 60000);
                    })(),
                    y: session.metadata?.duration || 0, // Keep duration in seconds
                    feedback: 'neutral', // Default to neutral
                    summary: session.standard_summary || 'No summary available',
                    context: session.metadata?.context || 'Unknown context',
                    dataName: session.metadata?.dataName || 'Unnamed'
                };
                if (session.metadata?.session_outcome?.parental_feedback) {
                    sessionData.feedback = session.metadata.session_outcome.parental_feedback.type.toLowerCase();
                }

                if (sessionData.feedback === 'positive') {
                    transformedData.positive.push(sessionData);
                } else if (sessionData.feedback === 'negative') {
                    transformedData.negative.push(sessionData);
                } else {
                    transformedData.neutral.push(sessionData);
                }
            });

            setSessionData(transformedData);
        } catch (error) {
            console.error('Error fetching session data:', error);
            setSessionData({ positive: [], negative: [], neutral: [] });
        }
    };

    useEffect(() => {
        if (sessionData && typeof sessionData === 'object') {
            setSessionChartData({
                datasets: [
                    {
                        label: 'Positive Sessions',
                        data: sessionData.positive,
                        backgroundColor: 'rgba(119,190,99,0.6)',
                        borderColor: 'rgba(119,190,99,1)',
                        barThickness: '16',
                    },
                    {
                        label: 'Neutral Sessions',
                        data: sessionData.neutral,
                        backgroundColor: 'rgba(255, 206, 86, 0.6)',
                        borderColor: 'rgba(255, 206, 86, 1)',
                        barThickness: '16',
                    },
                    {
                        label: 'Negative Sessions',
                        data: sessionData.negative,
                        backgroundColor: 'rgba(255, 99, 132, 0.6)',
                        borderColor: 'rgba(255, 99, 132, 1)',
                        barThickness: '16',
                    },
                ],
            });
        }
    }, [sessionData]);

    useEffect(() => {
        fetchSessionData(projectId);
    }, [projectId, currentStart, currentEnd]);

    const mapDays = ({ date }) => {
        const d = date.toDate();
        const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

        const hasSession = calendarData.some(session => {
            const sessionDate = new Date(session.timestamp);
            const sessionDateStr = `${sessionDate.getFullYear()}-${String(sessionDate.getMonth() + 1).padStart(2, '0')}-${String(sessionDate.getDate()).padStart(2, '0')}`;
            return sessionDateStr === dateStr;
        });

        if (hasSession) {
            const sessionTypes = {
                positive: 0,
                neutral: 0,
                negative: 0
            };

            calendarData.forEach(session => {
                const sessionDate = new Date(session.timestamp);
                const sessionDateStr = `${sessionDate.getFullYear()}-${String(sessionDate.getMonth() + 1).padStart(2, '0')}-${String(sessionDate.getDate()).padStart(2, '0')}`;
                if (sessionDateStr === dateStr) {
                    const feedback = session.metadata?.session_outcome?.parental_feedback?.type.toLowerCase() || 'neutral';
                    sessionTypes[feedback]++;
                }
            });

            const indicators = [];
            if (sessionTypes.positive > 0) indicators.push('positive');
            if (sessionTypes.neutral > 0) indicators.push('neutral');
            if (sessionTypes.negative > 0) indicators.push('negative');

            return {
                className: "has-record",
                children: (
                    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                        <span>{date.day}</span>
                        <div className="record-indicators">
                            {indicators.map((type, index) => (
                                <div key={type} className={`indicator ${type}`} />
                            ))}
                        </div>
                    </div>
                )
            };
        }
        return {};
    };


    return (
        <div className='session-chart-page'>
            <div className="calendar-controls">
                <div className="three-month-calendar">
                    <Calendar
                        numberOfMonths={3}
                        value={[currentStart, currentEnd]}
                        onChange={dates => {
                            if (Array.isArray(dates) && dates.length === 2) {
                                // If both dates are the same, set end to end of that day
                                let start = new Date(dates[0]);
                                let end = new Date(dates[1]);
                                
                                // Set times to start and end of day
                                start.setHours(0, 0, 0, 0);
                                // If end date is today, set to future time to include all data
                                const today = new Date();
                                if (end.toDateString() === today.toDateString()) {
                                    end.setHours(23, 59, 59, 999);
                                } else {
                                    end.setHours(23, 59, 59, 999);
                                }
                                setCurrentStart(start);
                                setCurrentEnd(end);
                                updateChartOptions(start, end);
                            }
                        }}
                        mapDays={mapDays}
                        range
                    />
                </div>
                <div className="time-controls">
                    <button onClick={handlePrevious}>Previous</button>
                    <button onClick={handleNext}>Next</button>
                </div>
            </div>
            <div className="session-chart-container">
                <Bar data={sessionChartData} options={sessionChartOptions} />
            </div>
        </div>
    );
};

export default SessionChart;
